import React from "react";
import { Box, Typography } from "@mui/material";

const FTE_OPTIONS = [0.25, 0.50, 0.75, 1.00];

const Step3FTEBar = ({ associate, fte, onFteChange, readOnly, currentFte, t }) => {
  // In edit mode: subtract the current request's own FTE so it isn't double-counted
  const baseUsed  = Math.max((associate?.usedFte ?? 0) - (currentFte ?? 0), 0);
  const usedPct   = Math.round((baseUsed / 1.0) * 100);
  const newTotal  = baseUsed + (fte ?? 0);
  const newPct    = Math.round((newTotal / 1.0) * 100);
  const isOverCap = newTotal > 1.0;

  return (
    <>
      {/* FTE selector */}
      <Typography sx={{
        fontFamily: "'DM Mono', monospace", fontSize: "8px",
        letterSpacing: "0.15em", textTransform: "uppercase", color: t.text3, mb: "8px",
      }}>
        Allocation (HC) <span style={{ color: t.accent }}>*</span>
      </Typography>

      <Box sx={{ display: "flex", gap: "8px", mb: "12px" }}>
        {FTE_OPTIONS.map((val) => {
          const isSelected = fte === val;
          return (
            <Box
              key={val}
              onClick={() => { if (readOnly) return; onFteChange?.(val); }}
              sx={{
                flex: 1, padding: "9px 0",
                display: "flex", alignItems: "center", justifyContent: "center",
                borderRadius: "3px", cursor: readOnly ? "default" : "pointer",
                border: isSelected ? `1px solid ${t.accent}88` : `1px solid ${t.border2}`,
                background: isSelected ? t.accent2 : t.surface2,
                transition: "all 0.15s",
              }}
            >
              <Typography sx={{
                fontFamily: "'DM Mono', monospace", fontSize: "14px",
                fontWeight: isSelected ? 700 : 400,
                color: isSelected ? t.accent : t.text2,
              }}>
                {val.toFixed(2)}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* FTE cap bar */}
      <Box sx={{
        background: t.surface2, border: `1px solid ${t.border}`,
        borderRadius: "3px", padding: "10px 12px", mb: "16px",
      }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: "5px" }}>
          <Typography sx={{
            fontFamily: "'DM Mono', monospace", fontSize: "8px",
            letterSpacing: "0.1em", textTransform: "uppercase",
            color: isOverCap ? "#c84040" : t.text3,
          }}>
            HC Cap — {associate?.fullName}
          </Typography>
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", color: isOverCap ? "#c84040" : t.text3 }}>
            {Math.min(newPct, 100)}%
          </Typography>
        </Box>

        <Box sx={{ height: "6px", background: t.surface3, borderRadius: "3px", overflow: "hidden", position: "relative", mb: "5px" }}>
          {/* Other allocations */}
          <Box sx={{
            position: "absolute", top: 0, left: 0,
            height: "100%", width: `${usedPct}%`,
            background: t.accent, borderRadius: "3px",
          }} />
          {/* This request */}
          {(fte ?? 0) > 0 && (
            <Box sx={{
              position: "absolute", top: 0, left: `${usedPct}%`,
              height: "100%",
              width: `${Math.min((fte ?? 0) * 100, 100 - usedPct)}%`,
              background: isOverCap ? "#c84040" : t.accent,
              opacity: 0.45, borderRadius: "3px",
            }} />
          )}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", color: t.text3 }}>
            Current: {baseUsed.toFixed(2)}
          </Typography>
          {(fte ?? 0) > 0 && (
            <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", color: isOverCap ? "#c84040" : t.text3 }}>
              +{fte} this request
            </Typography>
          )}
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", color: t.text3 }}>
            Cap: 1.0
          </Typography>
        </Box>

        {isOverCap && (
          <Box sx={{
            mt: "8px", background: "rgba(200,64,64,0.08)",
            border: "1px solid rgba(200,64,64,0.25)",
            borderRadius: "2px", padding: "6px 10px",
          }}>
            <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#c84040" }}>
              HC cap exceeded — reduce allocation
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
};

export default Step3FTEBar;